import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Loader, Segment } from 'semantic-ui-react';
import { Notification, STATUS_TYPES } from 'dyl-components';

import onboardingActions from 'actions/onboarding';
import ImportData from './ImportData';
import Integrations from './Integrations';
import ConfirmImportIntegrations from './ConfirmImportIntegrations';
import {
    CONTACT_METHOD_FIELDS, MAX_ANALYZED_ROWS, MAX_IMPORT_FILE_SIZE, NAME_FIELDS, STANDARD_IMPORT_FIELDS, readSpreadsheet
} from './importUtils';
import { IMPORT_INTEGRATIONS_SCREENS } from '../constants';
import { getLengthError, hasErrors } from '../validationUtils';

const DEFAULT_IMPORT = { mode: 'file', file_name: '', headers: [], sample_row: [], mappings: [], module: 'Lead', module_reason: '', duplicate_option: 'merge', analysis: null };

const validateImport = (importData, isReadingFile) => {
    if (importData.mode !== 'file') {
        return {};
    }
    if (!importData.headers.length) {
        return { file: isReadingFile ? null : 'Choose a file to import, or pick "I\'ll import later".' };
    }
    const mappedFields = importData.mappings.map(({ dyl_field }) => dyl_field);
    const errors = {};
    if (!mappedFields.some(field => NAME_FIELDS.includes(field))) {
        errors.mapping = 'Match at least one column to a name field (first, last or full name).';
    } else if (!mappedFields.some(field => CONTACT_METHOD_FIELDS.includes(field))) {
        errors.mapping = 'Match at least one column to an email or phone field so you can reach these contacts.';
    }
    return errors;
}

const validateIntegrations = ({ email_provider, web_form, lead_vendors }) => {
    const errors = {};
    if (!email_provider) {
        errors.email_provider = 'Choose an email provider';
    }
    if (web_form.enabled) {
        const nameError = getLengthError(web_form.name, 'Form name', 2, 64);
        if (nameError) {
            errors.web_form = nameError;
        }
    }
    if (lead_vendors.enabled && !lead_vendors.names.length) {
        errors.lead_vendors = 'Add at least one vendor, or turn this off';
    }
    return errors;
}

const ImportIntegrationsStep = ({ businessProfile = {}, crmDraft, draft, onDraftChange, onBack, onComplete }) => {
    const dispatch = useDispatch();

    const { user_id, isMappingColumns, isAnalyzingImport, isLoadingSampleImport, isGeneratingIntegrations } = useSelector(state => ({
        user_id: state.auth.user_id,
        isMappingColumns: state.onboarding.isMappingColumns,
        isAnalyzingImport: state.onboarding.isAnalyzingImport,
        isLoadingSampleImport: state.onboarding.isLoadingSampleImport,
        isGeneratingIntegrations: state.onboarding.isGeneratingIntegrations
    }));

    const [screen, setScreen] = useState(draft?.approval ? IMPORT_INTEGRATIONS_SCREENS.CONFIRM : (draft?.screen || IMPORT_INTEGRATIONS_SCREENS.IMPORT));
    const [importData, setImportData] = useState(draft?.import_data || DEFAULT_IMPORT);
    const [integrations, setIntegrations] = useState(draft?.integrations || null);
    const [approval, setApproval] = useState(draft?.approval || null);
    // File rows stay in memory only; drafts keep just the mapping and summary
    const [rows, setRows] = useState(null);
    const [isReadingFile, setIsReadingFile] = useState(false);

    // Custom fields approved in CRM Configuration are mapping targets too
    const customFields = crmDraft?.approval ? (crmDraft.field_group?.fields || []).filter(({ selected }) => selected) : [];
    const customFieldOptions = customFields.map(({ id, label }) => ({ key: `custom_${id}`, value: `custom:${id}`, text: `${label} (${crmDraft.field_group.name})` }));
    const fieldOptions = [...STANDARD_IMPORT_FIELDS, ...customFieldOptions];

    const generateIntegrations = useCallback(async () => {
        try {
            const result = await dispatch(onboardingActions.generateIntegrationSuggestions({
                industry: businessProfile.industry,
                website: businessProfile.website,
                business_name: businessProfile.business_name
            }));
            setIntegrations(result);
        } catch (e) {
            Notification.alert('Failed to suggest integrations', STATUS_TYPES.ERROR);
        }
    }, [dispatch, businessProfile.industry, businessProfile.website, businessProfile.business_name]);

    const hasIntegrations = !!integrations;
    useEffect(() => {
        if (!hasIntegrations) {
            generateIntegrations();
        }
    }, [hasIntegrations, generateIntegrations]);

    // An approval only covers what the user saw; any later edit requires approving again
    const approvalSnapshot = JSON.stringify({ importData, integrations });
    useEffect(() => {
        if (approval && approval.snapshot !== approvalSnapshot) {
            setApproval(null);
        }
    }, [approval, approvalSnapshot]);

    useEffect(() => {
        onDraftChange({ screen, import_data: importData, integrations, approval });
    }, [onDraftChange, screen, importData, integrations, approval]);

    const updateImport = (changes) => setImportData(current => ({ ...current, ...changes }));

    const runAnalysis = async (analysisRows, mappings) => {
        try {
            const analysis = await dispatch(onboardingActions.analyzeImportData({ rows: analysisRows.slice(0, MAX_ANALYZED_ROWS), mappings }));
            updateImport({ analysis });
        } catch (e) {
            Notification.alert('Failed to check the file for duplicates', STATUS_TYPES.ERROR);
        }
    }

    const loadFile = async ({ file_name, headers, rows: fileRows }) => {
        if (!headers.length || !fileRows.length) {
            Notification.alert('That file has no data rows to import', STATUS_TYPES.ERROR);
            return;
        }
        try {
            const { mappings, suggested_module, module_reason } = await dispatch(onboardingActions.suggestColumnMapping({
                headers,
                sample_rows: fileRows.slice(0, 20),
                custom_fields: customFields.map(({ id, label }) => ({ value: `custom:${id}`, text: label }))
            }));
            setRows(fileRows);
            updateImport({ file_name, row_count: fileRows.length, headers, sample_row: fileRows[0], mappings, module: suggested_module, module_reason, analysis: null });
            await runAnalysis(fileRows, mappings);
        } catch (e) {
            Notification.alert('Failed to match the file columns', STATUS_TYPES.ERROR);
        }
    }

    const onSelectFile = async (file) => {
        if (!file) {
            return;
        }
        if (file.size > MAX_IMPORT_FILE_SIZE) {
            Notification.alert('Files must be 10MB or smaller', STATUS_TYPES.ERROR);
            return;
        }
        setIsReadingFile(true);
        try {
            const { headers, rows: fileRows } = await readSpreadsheet(file);
            await loadFile({ file_name: file.name, headers, rows: fileRows });
        } catch (e) {
            Notification.alert('We couldn\'t read that file. Use a .csv, .xlsx or .xls file.', STATUS_TYPES.ERROR);
        } finally {
            setIsReadingFile(false);
        }
    }

    const onUseSample = async () => {
        try {
            const sample = await dispatch(onboardingActions.getSampleImportFile());
            await loadFile(sample);
        } catch (e) {
            Notification.alert('Failed to load the sample file', STATUS_TYPES.ERROR);
        }
    }

    const onChangeMapping = (index, dyl_field) => {
        const mappings = importData.mappings.map((mapping, mappingIndex) => mappingIndex === index
            ? { ...mapping, dyl_field, confidence: dyl_field ? 'high' : 'none', reason: dyl_field ? 'Matched by you' : 'Not imported' }
            : mapping);
        updateImport({ mappings });
        if (rows) {
            runAnalysis(rows, mappings);
        }
    }

    const onConfirm = () => {
        // TODO: upload the file and send the approved import and integrations to the backend once the endpoint exists
        const newApproval = { approved_by: user_id, approved_at: new Date().toISOString(), snapshot: approvalSnapshot };
        setApproval(newApproval);
        onComplete({ screen, import_data: importData, integrations, approval: newApproval });
        Notification.alert('Import and integrations saved as a draft', STATUS_TYPES.SUCCESS);
    }

    if (!hasIntegrations) {
        return (
            <Segment placeholder textAlign='center'>
                {isGeneratingIntegrations ? (
                    <Loader active inline='centered'>Getting your import and integration suggestions ready…</Loader>
                ) : (
                    <p>We couldn't load integration suggestions. <Button basic size='small' onClick={generateIntegrations}>Try Again</Button></p>
                )}
            </Segment>
        );
    }

    const importErrors = validateImport(importData, isReadingFile || isLoadingSampleImport);
    const integrationErrors = validateIntegrations(integrations);

    switch (screen) {
        case IMPORT_INTEGRATIONS_SCREENS.INTEGRATIONS:
            return (
                <Integrations
                    integrations={integrations}
                    errors={integrationErrors}
                    onChange={(changes) => setIntegrations(current => ({ ...current, ...changes }))}
                    canContinue={!hasErrors(integrationErrors)}
                    onBack={() => setScreen(IMPORT_INTEGRATIONS_SCREENS.IMPORT)}
                    onContinue={() => setScreen(IMPORT_INTEGRATIONS_SCREENS.CONFIRM)}
                />
            );
        case IMPORT_INTEGRATIONS_SCREENS.CONFIRM:
            return (
                <ConfirmImportIntegrations
                    importData={importData}
                    integrations={integrations}
                    isApprovedInitially={!!approval}
                    onBack={() => setScreen(IMPORT_INTEGRATIONS_SCREENS.INTEGRATIONS)}
                    onConfirm={onConfirm}
                />
            );
        default:
            return (
                <ImportData
                    importData={importData}
                    hasRows={!!rows}
                    fieldOptions={fieldOptions}
                    errors={importErrors}
                    isReadingFile={isReadingFile || isLoadingSampleImport}
                    isMapping={isMappingColumns}
                    isAnalyzing={isAnalyzingImport}
                    onChange={updateImport}
                    onSelectFile={onSelectFile}
                    onUseSample={onUseSample}
                    onChangeMapping={onChangeMapping}
                    canContinue={!hasErrors(importErrors) && !isReadingFile && !isMappingColumns}
                    onBack={onBack}
                    onContinue={() => setScreen(IMPORT_INTEGRATIONS_SCREENS.INTEGRATIONS)}
                />
            );
    }
}

export default ImportIntegrationsStep;
